import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { Row, Col } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

import { RawMultiCheckboxInput } from "components/form/legacyFields/multiCheckboxInput";

import * as theme from "../theme.css";

type OnChangeCallback<T extends string> = (key: T, value: boolean) => void;

interface GenericMultiCheckboxesCommonProps<T extends string> {
    labels: Record<T, string>;
    allLabel?: string;
    values: Record<T, Array<boolean>>;
}
type GenericMultiCheckboxesEditProps<T extends string> =
    GenericMultiCheckboxesCommonProps<T> & { onChange: OnChangeCallback<T> };
type GenericMultiCheckboxesReadOnlyProps<T extends string> =
    GenericMultiCheckboxesCommonProps<T> & { onChange?: OnChangeCallback<T> };
type GenericMultiCheckboxesProps<T extends string> =
    | ({ type: "EDIT" } & GenericMultiCheckboxesEditProps<T>)
    | ({ type: "READ_ONLY" } & GenericMultiCheckboxesReadOnlyProps<T>);

class GenericMultiCheckboxes<T extends string> extends React.PureComponent<
    GenericMultiCheckboxesProps<T>
> {
    render() {
        return (
            <Row>
                {this.renderAllCheckboxCol()}
                {this.renderCheckboxCols()}
            </Row>
        );
    }

    private renderCheckboxCols = (): Array<React.ReactNode> => {
        const { labels, type, values } = this.props;

        return Object.entries<string>(labels).map(([key, label]) => {
            return (
                <Col key={key} span={8} className={theme.checkboxHeight}>
                    <Checkbox
                        id={key}
                        type={type}
                        label={label}
                        values={values[key as T]}
                        onChange={(e: CheckboxChangeEvent) =>
                            this.handleChangeOne(key as T, e.target.checked)
                        }
                    />
                </Col>
            );
        });
    };

    private renderAllCheckboxCol = (): React.ReactNode => {
        const { allLabel, type } = this.props;

        if (allLabel) {
            return (
                <Col key="CHECK_ALL_VALUES" span={24}>
                    <Checkbox
                        id="CHECK_ALL_VALUES"
                        type={type}
                        label={allLabel}
                        values={this.flattenValues()}
                        onChange={(e: CheckboxChangeEvent) =>
                            this.handleChangeAll(e.target.checked)
                        }
                    />
                </Col>
            );
        } else {
            return undefined;
        }
    };

    private flattenValues = (): boolean[] => {
        const { values } = this.props;
        const arrays: boolean[][] = Object.values(values);
        return arrays.flat();
    };

    private handleChangeAll = (isChecked: boolean) => {
        const { labels, onChange } = this.props;

        if (onChange) {
            Object.keys(labels).forEach((key) => {
                // SAFETY: key is of type T, because labels is of type Record<T, string>
                onChange(key as T, isChecked);
            });
        }
    };

    private handleChangeOne = (key: T, isChecked: boolean) => {
        const { onChange } = this.props;

        if (onChange) {
            onChange(key, isChecked);
        }
    };
}

// Checkbox

interface CheckboxCommonProps {
    id: string;
    label: string;
    values: Array<boolean>;
}
type CheckboxEditProps = CheckboxCommonProps & {
    onChange: (e: CheckboxChangeEvent) => void;
};
type CheckboxReadOnlyProps = CheckboxCommonProps & {
    onChange?: (e: CheckboxChangeEvent) => void;
};
type CheckboxProps =
    | ({ type: "EDIT" } & CheckboxEditProps)
    | ({ type: "READ_ONLY" } & CheckboxReadOnlyProps);

function Checkbox(props: CheckboxProps) {
    const { type, id, label, values, onChange } = props;

    switch (type) {
        case "READ_ONLY": {
            return (
                <RawMultiCheckboxInput
                    type="READ_ONLY"
                    label={label}
                    values={values}
                />
            );
        }
        case "EDIT": {
            return (
                <RawMultiCheckboxInput
                    type="EDIT"
                    id={id}
                    label={label}
                    values={values}
                    value={values}
                    onCheckAllChange={onChange}
                    onChange={(_) => {}}
                />
            );
        }
        default:
            return assertNever(type);
    }
}

export { type GenericMultiCheckboxesProps, GenericMultiCheckboxes };
